import React from 'react';
import { isNil } from 'lodash';
import { PropTypes } from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { styled } from '@mui/material/styles';
import { CardSection } from '../Cards/CardSection';
import { CreditReasonType } from '../constants';
import Bankruptcy from './Details/Bankruptcy';
import PaymentRemarks from './Details/PaymentRemarks';
import Petitions from './Details/Petitions';

const CreditReasonAccordion = styled(Accordion)(() => ({
  width: '100%',
}));

const mapCreditDetailType = reasonType => {
  switch (reasonType) {
    case CreditReasonType.ENFORCEMENT_AGENCY:
      return Petitions;
    case CreditReasonType.PAYMENT_REMARKS:
      return PaymentRemarks;
    case CreditReasonType.BANKRUPTCY:
      return Bankruptcy;
    default:
      return null;
  }
};

const CreditReasonRowDetails = ({ reason, creditDecisionDetails }) => {
  const DetailsComponent = mapCreditDetailType(reason.reason);
  if (isNil(DetailsComponent)) {
    return null;
  }
  return <DetailsComponent {...creditDecisionDetails} />;
};

CreditReasonRowDetails.propTypes = {
  reason: PropTypes.shape({
    type: PropTypes.string,
    reason: PropTypes.string,
  }),
  creditDecisionDetails: PropTypes.shape({
    paymentRemarks: PropTypes.object,
    petitions: PropTypes.object,
    bankruptcy: PropTypes.object,
  }),
};

const ExpandableCreditReason = ({ reason, creditDecisionDetails }) => {
  const { t } = useTranslation();
  return (
    <CreditReasonAccordion square key={reason.reason}>
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        aria-controls={`${reason.reason}-content`}
        id={`${reason.reason}-header`}
      >
        {t(`enums.creditDecisionReason.values.${reason.reason}`, reason.reason)}
      </AccordionSummary>
      <AccordionDetails>
        <CreditReasonRowDetails reason={reason} creditDecisionDetails={creditDecisionDetails} />
      </AccordionDetails>
    </CreditReasonAccordion>
  );
};

ExpandableCreditReason.propTypes = {
  reason: CreditReasonRowDetails.propTypes.reason.isRequired,
  creditDecisionDetails: CreditReasonRowDetails.propTypes.creditDecisionDetails,
};

export const CreditReason = ({ reasonType, reasons, creditDecisionDetails, layout: { card } }) => {
  const { t } = useTranslation();
  const title = t(`enums.creditDecisionReasonType.values.${reasonType}`, reasonType);

  return (
    <CardSection title={title} key={reasonType} {...card}>
      {creditDecisionDetails
        ? reasons.map(reason => (
            <ExpandableCreditReason key={reason.reason} reason={reason} creditDecisionDetails={creditDecisionDetails} />
          ))
        : reasons.map(reason => t(`enums.creditDecisionReason.values.${reason.reason}`, reason.reason)).join(',')}
    </CardSection>
  );
};

CreditReason.defaultProps = {
  layout: {
    card: {
      size: 'm',
    },
  },
};

CreditReason.propTypes = {
  reasonType: PropTypes.string,
  reasons: PropTypes.arrayOf(CreditReasonRowDetails.propTypes.reason),
  creditDecisionDetails: CreditReasonRowDetails.propTypes.creditDecisionDetails,
  layout: PropTypes.shape({
    card: {
      size: CardSection.propTypes.size,
      contentAlign: CardSection.propTypes.contentAlign,
    },
  }),
};

export default CreditReason;
